import React, { useState, useCallback } from 'react';
import { View, TextInput, TouchableOpacity, Text, StyleSheet, Platform } from 'react-native';
import { useChat } from '@livekit/components-react';
import World from 'components/World';

export default function LiveKitTextInput({ disabled = false, onSent }) {
  const [textMessage, setTextMessage] = useState('');
  const { send, isSending } = useChat();
  const [isHoveredSend, setIsHoveredSend] = useState(false);

  // combine sending state and external disabled
  const effectiveDisabled = isSending || disabled;

  // memoize send handler, and notify parent to disable after send
  const onSend = useCallback(() => {
    const msg = textMessage.trim();
    if (!msg) return;
    send(msg);
    setTextMessage('');
    // disable input after send
    if (typeof onSent === 'function') {
      onSent();
    }
    World.runCommand(
      `livekit_final_response dummy ${msg.replace(/ /g, "_")} true player`
    );

  }, [textMessage, send, onSent]);

  // web-only hover handlers
  const hoverProps = Platform.OS === 'web' && !effectiveDisabled
    ? {
        onMouseEnter: () => setIsHoveredSend(true),
        onMouseLeave: () => setIsHoveredSend(false),
      }
    : {};

  // web vs native event handlers for sending
  const sendHandlers = Platform.OS === 'web'
    ? { onKeyPress: e => e.nativeEvent.key === 'Enter' && onSend() }
    : { onSubmitEditing: onSend };

  // web-only disable prop support
  const inputProps = Platform.OS === 'web'
    ? { disabled: effectiveDisabled }
    : { editable: !effectiveDisabled };

  return (
    <View style={styles.container}>
      <TextInput
        {...inputProps}
        style={styles.textInput}
        value={textMessage}
        onChangeText={setTextMessage}
        placeholder="Type here to respond..."
        placeholderTextColor="#a1a1a1"
        returnKeyType="send"
        {...sendHandlers}
      />
      <TouchableOpacity
        style={[
          styles.sendButton,
          isHoveredSend && styles.sendButtonHover,
          effectiveDisabled && styles.sendButtonDisabled,
        ]}
        onPress={onSend}
        disabled={effectiveDisabled}
        {...hoverProps}
      >
        <Text
          style={[
            styles.sendButtonText,
            isHoveredSend && styles.sendButtonTextHover,
            effectiveDisabled && styles.sendButtonTextDisabled,
          ]}
        >
          Send
        </Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.8)',
    borderTopWidth: 1,
    borderTopColor: 'rgba(189,222,54,0.2)',
  },
  textInput: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderWidth: 1,
    borderColor: 'rgba(189,222,54,0.2)',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    color: '#BDDE36',
    ...(Platform.OS === 'web' ? { outlineWidth: 0 } : {}),
  },
  sendButton: {
    marginLeft: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(189,222,54,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonHover: {
    backgroundColor: '#A8C434',
    borderColor: '#A8C434',
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
  sendButtonText: {
    color: '#CAFF33',
    fontWeight: '600',
    fontSize: 13,
  },
  sendButtonTextHover: {
    color: '#000',
  },
  sendButtonTextDisabled: {
    color: '#888',
  },
});
